import {View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useNavigation} from '@react-navigation/native'

import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
  type NavigationProp,
} from '#/lib/routes/types'
import {useMatrixChatRequests} from '#/state/matrix/hooks/useMatrixChatRequests'
import {atoms as a, useBreakpoints, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {ArrowLeft_Stroke2_Corner0_Rounded as ArrowLeftIcon} from '#/components/icons/Arrow'
import {Message_Stroke2_Corner0_Rounded as MessageIcon} from '#/components/icons/Message'
import * as Layout from '#/components/Layout'
import {Text} from '#/components/Typography'
import {SecureRequestListItem} from './components/SecureRequestListItem'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'SecureChatInbox'>

export function SecureChatInboxScreen({}: Props) {
  const {_} = useLingui()
  const t = useTheme()
  const {gtTablet} = useBreakpoints()
  const navigation = useNavigation<NavigationProp>()
  const {pendingRequests, acceptRequest, rejectRequest} =
    useMatrixChatRequests()

  return (
    <Layout.Screen testID="secureChatInboxScreen">
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content align={gtTablet ? 'left' : 'platform'}>
          <Layout.Header.TitleText>
            <Trans>Chat requests</Trans>
          </Layout.Header.TitleText>
        </Layout.Header.Content>
        <Layout.Header.Slot />
      </Layout.Header.Outer>

      {pendingRequests.length === 0 ? (
        <Layout.Center>
          <View style={[a.pt_3xl, a.align_center]}>
            <MessageIcon width={48} fill={t.palette.primary_500} />
            <Text style={[a.pt_md, a.pb_sm, a.text_2xl, a.font_bold]}>
              <Trans comment="Title message shown in chat requests inbox when it's empty">
                Inbox zero!
              </Trans>
            </Text>
            <Text
              style={[
                a.text_md,
                a.pb_xl,
                a.text_center,
                a.leading_snug,
                t.atoms.text_contrast_medium,
              ]}>
              <Trans>You don't have any chat requests at the moment.</Trans>
            </Text>
            <Button
              variant="solid"
              color="secondary"
              size="small"
              label={_(msg`Go back`)}
              onPress={() => {
                if (navigation.canGoBack()) {
                  navigation.goBack()
                } else {
                  navigation.navigate('Messages', {animation: 'pop'})
                }
              }}>
              <ButtonIcon icon={ArrowLeftIcon} />
              <ButtonText>
                <Trans>Back to Chats</Trans>
              </ButtonText>
            </Button>
          </View>
        </Layout.Center>
      ) : (
        <Layout.Content>
          {pendingRequests.map(room => (
            <SecureRequestListItem
              key={room.roomId}
              room={room}
              onAccept={acceptRequest}
              onReject={rejectRequest}
            />
          ))}
        </Layout.Content>
      )}
    </Layout.Screen>
  )
}
